import { useApolloClient } from "@apollo/client";
import { getDataOrNull } from "@msys/common";
import React from "react";
import { namedOperations } from "../../../clients/graphqlTypes.js";
import {
  BrandPreferenceFragment,
  useBrandPreferencesQuery,
  useDeleteProductTypeBrandPreferencesMutation,
  useSetBrandPreferenceMutation,
} from "./BrandPreferences.generated.js";
import { evictRankedProductSearches } from "./useProductPreferences.js";

/**
 * The caller's brand preference entries, unfiltered — global and per product type, at both
 * scopes — plus the two entry writes. One read serves the directory's counts.
 */
export function useBrandPreferences() {
  const client = useApolloClient();

  const query = useBrandPreferencesQuery({ client });

  // `previousData` carries the list through the refetch a write triggers.
  const result = getDataOrNull(
    (query.data ?? query.previousData)?.pimBrandPreferences
  );

  const writeOptions = {
    client,
    refetchQueries: [
      namedOperations.Query.BrandPreferences,
      namedOperations.Query.PreferenceList,
    ],
    awaitRefetchQueries: true,
    update: evictRankedProductSearches,
  };

  const [setBrandPreference, setState] =
    useSetBrandPreferenceMutation(writeOptions);
  const [deleteBrandPreferences, deleteState] =
    useDeleteProductTypeBrandPreferencesMutation(writeOptions);

  const loading = query.loading && result === null;
  const writing = setState.loading || deleteState.loading;

  // Memoized: consumers put this object in dependency arrays.
  return React.useMemo(
    () => ({
      brandPreferences: result?.preferences ?? EMPTY_BRAND_PREFERENCES,
      loading,
      failed: !loading && result === null,
      setBrandPreference,
      deleteBrandPreferences,
      writing,
    }),
    [result, loading, setBrandPreference, deleteBrandPreferences, writing]
  );
}

const EMPTY_BRAND_PREFERENCES: readonly BrandPreferenceFragment[] = [];

export type UseBrandPreferences = ReturnType<typeof useBrandPreferences>;
